import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ApiError } from '../../../abstract/api-error';
import { EpaChemicalsService } from '../../../core/data-services/epa-chemicals.service';
import { withChanges } from '../../../core/utils/model-utils';
import { EpaChemical } from '../../../core/data-models/epa-chemical.model';
import { EpaChemicalCustomName } from '../../../core/data-models/epa-chemical-custom-name.model';
import { EpaChemicalLabel } from '../../../core/data-models/epa-chemical-label.model';
import { CustomNameType } from '../../../core/data-models/custom-name-type.model';
import { EpaChemicalUpdateRequest } from '../../../core/request-models/epa-chemical-update-request.model';

/**
 * EPA Chemical Detail Component
 *
 * Displays detailed view of a single EPA chemical with:
 * - Read-only EPA data fields
 * - Editable admin fields (sdsUrl, adminNotes, isHidden)
 * - Custom names management (add, remove, set primary)
 * - Label history display
 */
@Component({
  selector: 'app-epa-chemical-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './epa-chemical-detail.component.html',
  styleUrl: './epa-chemical-detail.component.scss'
})
export class EpaChemicalDetailComponent implements OnInit {
  chemicalId: string | null = null;
  chemical: EpaChemical | null = null;
  loading = true;
  saving = false;
  errorMessage = '';
  successMessage = '';

  // Admin fields form
  adminForm: FormGroup;

  // Custom name form state
  showAddNameForm = false;
  newCustomName = '';
  newCustomNameType: CustomNameType = 'alias';
  newCustomNamePrimary = false;
  addingName = false;

  // Label management state
  showAddLabelForm = false;
  newLabelUrl = '';
  newLabelName = '';
  newLabelDate = '';
  newLabelSetCurrent = true;
  addingLabel = false;
  deletingLabelId: string | null = null;
  settingCurrentLabelId: string | null = null;

  // PPLS sync state
  syncingPpls = false;

  // Name type options
  nameTypeOptions: { value: CustomNameType; label: string }[] = [
    { value: 'common_name', label: 'Common Name' },
    { value: 'trade_name', label: 'Trade Name' },
    { value: 'alias', label: 'Alias' }
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private epaChemicalsService: EpaChemicalsService
  ) {
    this.adminForm = this.fb.group({
      sdsUrl: [''],
      adminNotes: [''],
      isHidden: [false]
    });
  }

  ngOnInit(): void {
    this.chemicalId = this.route.snapshot.paramMap.get('id');
    if (this.chemicalId) {
      this.loadChemical();
    } else {
      this.errorMessage = 'Chemical ID not provided';
      this.loading = false;
    }
  }

  /**
   * Load chemical data
   */
  loadChemical(): void {
    this.loading = true;
    this.errorMessage = '';

    this.epaChemicalsService.getById(this.chemicalId!).subscribe({
      next: (chemical) => {
        if (!chemical) {
          this.errorMessage = 'EPA chemical not found';
          this.loading = false;
          return;
        }

        this.chemical = chemical;

        // Populate admin form
        this.adminForm.patchValue({
          sdsUrl: chemical.sdsUrl || '',
          adminNotes: chemical.adminNotes || '',
          isHidden: chemical.isHidden || false
        });

        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load EPA chemical:', error);
        this.errorMessage = 'Failed to load EPA chemical';
        this.loading = false;
      }
    });
  }

  /**
   * Save admin fields
   */
  saveAdminFields(): void {
    if (this.saving || !this.chemicalId) return;

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.adminForm.getRawValue();
    const updateData: EpaChemicalUpdateRequest = {
      sdsUrl: formValue.sdsUrl || null,
      adminNotes: formValue.adminNotes || null,
      isHidden: formValue.isHidden
    };

    this.epaChemicalsService.updateCustomFields(this.chemicalId, updateData).subscribe({
      next: (updated) => {
        // The PUT returns the row without custom names and labels, so only its admin fields are taken.
        if (updated && this.chemical) {
          this.chemical = withChanges(this.chemical, { sdsUrl: updated.sdsUrl, adminNotes: updated.adminNotes, isHidden: updated.isHidden });
        }
        this.successMessage = 'Admin fields updated successfully';
        this.saving = false;
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to update admin fields:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to update admin fields';
        this.saving = false;
      }
    });
  }

  /**
   * Toggle add custom name form
   */
  toggleAddNameForm(): void {
    this.showAddNameForm = !this.showAddNameForm;
    if (!this.showAddNameForm) {
      this.resetNameForm();
    }
  }

  /**
   * Reset custom name form
   */
  private resetNameForm(): void {
    this.newCustomName = '';
    this.newCustomNameType = 'alias';
    this.newCustomNamePrimary = false;
  }

  /**
   * Add custom name
   */
  addCustomName(): void {
    if (this.addingName || !this.chemicalId || !this.newCustomName.trim()) return;

    this.addingName = true;
    this.errorMessage = '';

    this.epaChemicalsService.addCustomName(this.chemicalId, {
      name: this.newCustomName.trim(),
      nameType: this.newCustomNameType,
      isPrimary: this.newCustomNamePrimary
    }).subscribe({
      next: (customName) => {
        if (!customName) {
          this.addingName = false;
          return;
        }
        // Add to local list
        if (!this.chemical!.customNames) {
          this.chemical!.customNames = [];
        }

        // If setting as primary, unset others
        if (customName.isPrimary && this.chemical!.customNames) {
          this.chemical!.customNames.forEach(n => n.isPrimary = false);
        }

        this.chemical!.customNames.push(customName);
        this.successMessage = 'Custom name added successfully';
        this.addingName = false;
        this.showAddNameForm = false;
        this.resetNameForm();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to add custom name:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to add custom name';
        this.addingName = false;
      }
    });
  }

  /**
   * Remove custom name
   */
  removeCustomName(customName: EpaChemicalCustomName): void {
    if (!this.chemicalId) return;

    if (!confirm(`Are you sure you want to remove "${customName.name}"?`)) {
      return;
    }

    this.epaChemicalsService.removeCustomName(this.chemicalId, customName.id).subscribe({
      next: () => {
        // Remove from local list
        if (this.chemical!.customNames) {
          this.chemical!.customNames = this.chemical!.customNames.filter(
            n => n.id !== customName.id
          );
        }
        this.successMessage = 'Custom name removed successfully';
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to remove custom name:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to remove custom name';
      }
    });
  }

  /**
   * Get display name for chemical
   */
  getDisplayName(): string {
    if (!this.chemical) return '';
    return this.chemical.displayName || this.chemical.name || '';
  }

  /**
   * Get current label - falls back to most recent label if none marked as current
   */
  getCurrentLabel(): EpaChemicalLabel | null {
    if (!this.chemical?.labels || this.chemical.labels.length === 0) return null;

    // First try to find the one marked as current
    const current = this.chemical.labels.find(l => l.isCurrent);
    if (current) return current;

    // Fall back to most recent label by accepted date
    const sorted = [...this.chemical.labels].sort((a, b) => {
      if (!a.acceptedDate) return 1;
      if (!b.acceptedDate) return -1;
      return new Date(b.acceptedDate).getTime() - new Date(a.acceptedDate).getTime();
    });
    return sorted[0] || null;
  }

  /**
   * Get label history (sorted by accepted date, current excluded)
   */
  getLabelHistory(): EpaChemicalLabel[] {
    if (!this.chemical?.labels) return [];
    return this.chemical.labels
      .filter(l => !l.isCurrent)
      .sort((a, b) => {
        if (!a.acceptedDate) return 1;
        if (!b.acceptedDate) return -1;
        return new Date(b.acceptedDate).getTime() - new Date(a.acceptedDate).getTime();
      });
  }

  /**
   * Get primary custom name (if any)
   */
  getPrimaryCustomName(): EpaChemicalCustomName | null {
    if (!this.chemical?.customNames) return null;
    return this.chemical.customNames.find(n => n.isPrimary) || null;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | Date | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Format datetime for display
   */
  formatDateTime(dateString: string | Date | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get signal word badge class
   */
  getSignalWordClass(signalWord: string | null | undefined): string {
    switch (signalWord?.toUpperCase()) {
      case 'DANGER':
        return 'bg-danger';
      case 'WARNING':
        return 'bg-warning text-dark';
      case 'CAUTION':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string | null | undefined): string {
    switch (status) {
      case 'Active':
        return 'bg-success';
      case 'Cancelled':
        return 'bg-danger';
      case 'Pending':
        return 'bg-warning text-dark';
      case 'removed-from-epa':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get name type display label
   */
  getNameTypeLabel(nameType: string | null | undefined): string {
    const option = this.nameTypeOptions.find(o => o.value === nameType);
    return option?.label || nameType || '';
  }

  /**
   * Format semicolon-delimited string to array
   */
  formatSemicolonList(value: string | null | undefined): string[] {
    if (!value) return [];
    return value.split(';').map(s => s.trim()).filter(s => s.length > 0);
  }

  /**
   * Sync this chemical from the PPLS API
   * Fetches enriched data including active ingredients, sites, pests, and labels
   */
  syncFromPpls(): void {
    if (this.syncingPpls || !this.chemicalId) return;

    this.syncingPpls = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.epaChemicalsService.syncFromPpls(this.chemicalId).subscribe({
      next: (result) => {
        // Keep the loaded custom names and labels when the hub didn't send them (nothing new from PPLS)
        if (result.chemical && this.chemical) {
          const synced = result.chemical;
          this.chemical = withChanges(synced, {
            customNames: synced.customNames ?? this.chemical.customNames,
            labels: synced.labels ?? this.chemical.labels,
          });
        }
        this.successMessage = result.message || 'Synced from PPLS API successfully';
        this.syncingPpls = false;
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to sync from PPLS:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to sync from PPLS API';
        this.syncingPpls = false;
      }
    });
  }

  // ============================================
  // LABEL MANAGEMENT
  // ============================================

  /**
   * Toggle add label form
   */
  toggleAddLabelForm(): void {
    this.showAddLabelForm = !this.showAddLabelForm;
    if (!this.showAddLabelForm) {
      this.resetLabelForm();
    }
  }

  /**
   * Reset label form
   */
  private resetLabelForm(): void {
    this.newLabelUrl = '';
    this.newLabelName = '';
    this.newLabelDate = '';
    this.newLabelSetCurrent = true;
  }

  /**
   * Add a label
   */
  addLabel(): void {
    if (this.addingLabel || !this.chemicalId || !this.newLabelUrl.trim()) return;

    this.addingLabel = true;
    this.errorMessage = '';

    this.epaChemicalsService.addLabel(this.chemicalId, {
      labelUrl: this.newLabelUrl.trim(),
      labelName: this.newLabelName.trim() || undefined,
      acceptedDate: this.newLabelDate || undefined,
      setAsCurrent: this.newLabelSetCurrent
    }).subscribe({
      next: (label) => {
        if (!label) {
          this.addingLabel = false;
          return;
        }
        if (!this.chemical!.labels) {
          this.chemical!.labels = [];
        }

        // If setting as current, unset others locally
        if (label.isCurrent) {
          this.chemical!.labels.forEach(l => l.isCurrent = false);
        }

        this.chemical!.labels.push(label);
        this.successMessage = 'Label added successfully';
        this.addingLabel = false;
        this.showAddLabelForm = false;
        this.resetLabelForm();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to add label:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to add label';
        this.addingLabel = false;
      }
    });
  }

  /**
   * Delete a label
   */
  deleteLabel(label: EpaChemicalLabel): void {
    if (!this.chemicalId) return;

    if (!confirm(`Are you sure you want to delete this label?`)) {
      return;
    }

    this.deletingLabelId = label.id;

    this.epaChemicalsService.deleteLabel(this.chemicalId, label.id).subscribe({
      next: () => {
        if (this.chemical!.labels) {
          this.chemical!.labels = this.chemical!.labels.filter(l => l.id !== label.id);
        }
        this.successMessage = 'Label deleted successfully';
        this.deletingLabelId = null;
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to delete label:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to delete label';
        this.deletingLabelId = null;
      }
    });
  }

  /**
   * Toggle a label's current status
   */
  setCurrentLabel(label: EpaChemicalLabel): void {
    if (!this.chemicalId) return;

    this.settingCurrentLabelId = label.id;

    this.epaChemicalsService.setCurrentLabel(this.chemicalId, label.id).subscribe({
      next: (result) => {
        // Replace labels array with fresh data from server
        if (this.chemical) {
          this.chemical.labels = result.labels;
        }
        this.successMessage = label.isCurrent ? 'Label unmarked as current' : 'Label set as current';
        this.settingCurrentLabelId = null;
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to toggle current label:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to toggle current label';
        this.settingCurrentLabelId = null;
      }
    });
  }

  /**
   * Navigate back to list
   */
  goBack(): void {
    this.router.navigate(['/epa-chemicals']);
  }

  /**
   * Clear success message after delay
   */
  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }
}
